import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Wallet, ClipboardList, CheckCircle, XCircle, Calendar, User, RefreshCw, FileText, Building2, Download, Send, Banknote, Clock, Search, X, CalendarDays } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Avans verisi form_data.form_data altında saklanıyor
const getAvans = (r) => (r.form_data && r.form_data.form_data) ? r.form_data.form_data : (r.form_data || {});

const izinTuruLabel = (t) => ({ yillik: 'Yıllık İzin', mazeret: 'Ücretli İzin', ucretsiz: 'Ücretsiz İzin' }[t] || 'İzin');
const getLeaveDoc = (av) => (Array.isArray(av.izinBelgeleri) && av.izinBelgeleri.length && av.izinBelgeleri[0] && av.izinBelgeleri[0].data) ? av.izinBelgeleri[0] : null;
const attendanceStatusOptions = [
  { value: 'annual', label: 'Yıllık İzin', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  { value: 'paid_leave', label: 'Ücretli İzin', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { value: 'unpaid_leave', label: 'Ücretsiz İzin', color: 'bg-gray-200 text-gray-800 border-gray-400' },
  { value: 'sick', label: 'Raporlu', color: 'bg-orange-100 text-orange-800 border-orange-300' },
];

const formatTL = (v) => {
  const n = parseInt(v);
  return isNaN(n) ? (v || '-') : `${n.toLocaleString('tr-TR')} TL`;
};

const formatDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return d;
  }
};

// Base64 / data URI belgeyi Blob'a çevirip yeni sekmede aç
const openBase64Doc = (doc) => {
  const dataUri = typeof doc === 'string' ? doc : (doc && doc.data);
  if (!dataUri || !dataUri.includes(',')) {
    toast.error('Belge bulunamadı');
    return;
  }
  try {
    const arr = dataUri.split(',');
    const mime = (arr[0].match(/:(.*?);/) || [])[1] || 'application/octet-stream';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8 = new Uint8Array(n);
    while (n--) u8[n] = bstr.charCodeAt(n);
    const blob = new Blob([u8], { type: mime });
    window.open(URL.createObjectURL(blob), '_blank');
  } catch (e) {
    toast.error('Belge açılamadı');
  }
};

// Base64 / data URI belgeyi indir
const downloadBase64Doc = (doc) => {
  const dataUri = typeof doc === 'string' ? doc : (doc && doc.data);
  if (!dataUri || !dataUri.includes(',')) {
    toast.error('Belge bulunamadı');
    return;
  }
  try {
    const a = document.createElement('a');
    a.href = dataUri;
    a.download = (doc && doc.name) || 'belge';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (e) {
    toast.error('Belge indirilemedi');
  }
};

const HRPersonnelRequests = () => {
  const [loading, setLoading] = useState(true);
  const [avansRequests, setAvansRequests] = useState([]);
  const [forwardedRequests, setForwardedRequests] = useState([]);
  const [givenAdvances, setGivenAdvances] = useState([]);
  const [rejectedRequests, setRejectedRequests] = useState([]);
  const [overtimeInbox, setOvertimeInbox] = useState([]);
  const [overtimeApproved, setOvertimeApproved] = useState([]);
  const [overtimeRejected, setOvertimeRejected] = useState([]);
  const [leaveInbox, setLeaveInbox] = useState([]);
  const [leaveApproved, setLeaveApproved] = useState([]);
  const [leaveRejected, setLeaveRejected] = useState([]);
  // İzin onay modalı
  const [leaveTarget, setLeaveTarget] = useState(null);
  const [selectedLeaveDays, setSelectedLeaveDays] = useState([]);
  const [selectedAttendanceStatus, setSelectedAttendanceStatus] = useState('annual');
  const [leaveNote, setLeaveNote] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  // Arama & filtreleme (isim + tarih) — tüm sekmelerde geçerli
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');

  // Talep eden ismini farklı alanlardan topla
  const getRequesterName = (r) => {
    const inner = (r.form_data && r.form_data.form_data) ? r.form_data.form_data : (r.form_data || {});
    return (r.requester_name || r.original_requester_name || inner.talepEdenAdi || inner.mesaiYapanAdi || '').toString();
  };
  // Talep tarihinin YYYY-MM-DD kısmı (oluşturma tarihi)
  const getRequestDateISO = (r) => (r.created_at ? String(r.created_at).slice(0, 10) : '');

  // İsim + tarih filtresini bir listeye uygula
  const applyFilters = useCallback((list) => {
    const term = searchTerm.trim().toLocaleLowerCase('tr-TR');
    return (list || []).filter((r) => {
      const nameOk = !term || getRequesterName(r).toLocaleLowerCase('tr-TR').includes(term);
      const dateOk = !filterDate || getRequestDateISO(r) === filterDate;
      return nameOk && dateOk;
    });
  }, [searchTerm, filterDate]);

  const filtersActive = searchTerm.trim() !== '' || filterDate !== '';

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const [inboxRes, forwardedRes, givenRes, rejectedRes, otApprovedRes, otRejectedRes, lvApprovedRes, lvRejectedRes] = await Promise.all([
        axios.get(`${API}/hr/hr-requests/inbox?light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hr/hr-requests/forwarded-accounting?light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hr/hr-requests/given-advances?light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hr/hr-requests/rejected?light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hr/hr-requests/overtime-approved?light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hr/hr-requests/overtime-rejected?light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hr/hr-requests/leave-approved?light=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hr/hr-requests/leave-rejected?light=true`, { headers: getAuthHeaders() }),
      ]);
      const inbox = inboxRes.data || [];
      setAvansRequests(inbox.filter((r) => r.form_template_id === 'avans'));
      setOvertimeInbox(inbox.filter((r) => r.form_template_id === 'overtime'));
      setLeaveInbox(inbox.filter((r) => r.form_template_id === 'leave'));
      setForwardedRequests(forwardedRes.data || []);
      setGivenAdvances(givenRes.data || []);
      setRejectedRequests((rejectedRes.data || []).filter((r) => r.form_template_id === 'avans'));
      setOvertimeApproved(otApprovedRes.data || []);
      setOvertimeRejected(otRejectedRes.data || []);
      setLeaveApproved(lvApprovedRes.data || []);
      setLeaveRejected(lvRejectedRes.data || []);
      // Bekleyen talepleri görüldü olarak işaretle (menü sayacı sıfırlanır)
      try {
        await axios.post(`${API}/hr/personnel-requests/mark-seen`, {}, { headers: getAuthHeaders() });
      } catch (markErr) {
        console.error('Talepler görüldü işaretlenemedi:', markErr);
      }
    } catch (e) {
      console.error('Talepler yüklenemedi:', e);
      toast.error('Talepler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (req) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/hr/hr-requests/${req.id}/action?action=forward_accounting&note=${encodeURIComponent("İK onayladı ve Muhasebeye iletti")}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Talep onaylandı ve Muhasebeye iletildi');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveOvertime = async (req) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/hr/hr-requests/${req.id}/action?action=hr_approve&note=${encodeURIComponent("İK fazla mesaiyi onayladı")}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Fazla mesai talebi onaylandı');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Lütfen red sebebini girin');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/hr/hr-requests/${rejectTarget.id}/action?action=reject&note=${encodeURIComponent(rejectReason)}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Talep reddedildi');
      setRejectTarget(null);
      setRejectReason('');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  // İzin onay modalını aç (günleri ve varsayılan durumu hesapla)
  const openLeaveApproval = (req) => {
    const av = getAvans(req);
    const statusMap = { yillik: 'annual', mazeret: 'paid_leave', ucretsiz: 'unpaid_leave' };
    setSelectedAttendanceStatus(statusMap[av.izinTuru] || 'annual');
    setLeaveNote('');
    const days = [];
    if (av.izinBaslangic && av.izinBitis) {
      const cur = new Date(av.izinBaslangic);
      const end = new Date(av.izinBitis);
      let guard = 0;
      while (cur <= end && guard < 400) {
        days.push(cur.toISOString().split('T')[0]);
        cur.setDate(cur.getDate() + 1);
        guard += 1;
      }
    }
    setSelectedLeaveDays(days);
    setLeaveTarget(req);
  };

  const toggleLeaveDay = (dayStr) => {
    setSelectedLeaveDays((prev) => prev.includes(dayStr) ? prev.filter((d) => d !== dayStr) : [...prev, dayStr]);
  };

  const confirmLeaveApproval = async () => {
    if (selectedLeaveDays.length === 0) {
      toast.error('Lütfen en az bir izin günü seçin');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/hr/hr-requests/${leaveTarget.id}/approve-leave`,
        { selected_days: selectedLeaveDays, attendance_status: selectedAttendanceStatus, note: leaveNote || '' },
        { headers: getAuthHeaders() }
      );
      const label = attendanceStatusOptions.find((o) => o.value === selectedAttendanceStatus)?.label || 'İzin';
      toast.success(`İzin onaylandı ve ${selectedLeaveDays.length} gün "${label}" olarak işlendi`);
      setLeaveTarget(null);
      setSelectedLeaveDays([]);
      setSelectedAttendanceStatus('annual');
      setLeaveNote('');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  const renderBranchApprovedLine = (req) => {
    const ba = req.branch_approval || {};
    const by = ba.forwarded_by_name || ba.approved_by_name || ba.manager_name;
    if (!by && !ba.forwarded) return null;
    return (
      <div className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-2 py-1 w-fit" data-testid={`branch-approved-line-${req.id}`}>
        <Building2 className="h-3 w-3" />
        <span>Şube onayı: <strong>{by || 'Şube Yönetimi'}</strong>{ba.timestamp ? ` • ${formatDate(ba.timestamp)}` : ''}</span>
      </div>
    );
  };

  const renderAvansCard = (req) => {
    const av = getAvans(req);
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-emerald-500" data-testid={`hr-avans-request-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-100">
                <User className="h-4 w-4 text-emerald-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{req.requester_name || av.talepEdenAdi || 'Çalışan'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {formatDate(req.created_at)}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
              Şube Onayladı · İK Onayı Bekliyor
            </Badge>
          </div>

          {req.requester_branch_name && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Building2 className="h-3 w-3" /> {req.requester_branch_name}
            </p>
          )}

          {renderBranchApprovedLine(req)}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 rounded-lg p-3">
            <div>
              <p className="text-xs text-gray-500">Avans Miktarı</p>
              <p className="font-bold text-emerald-700">{formatTL(av.avansMiktari)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Taksit Sayısı</p>
              <p className="font-medium">{av.taksitSayisi || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Form No</p>
              <p className="font-medium">{req.form_number || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Ödeme Tarihi</p>
              <p className="font-medium">{av.odemeTarihi || '-'}</p>
            </div>
          </div>

          {av.avansSebebi && (
            <div>
              <p className="text-xs text-gray-500">Avans Sebebi</p>
              <p className="text-sm text-gray-700">{av.avansSebebi}</p>
            </div>
          )}

          {req.branch_approval?.note && (
            <div>
              <p className="text-xs text-gray-500">Şube Notu</p>
              <p className="text-sm text-gray-700">{req.branch_approval.note}</p>
            </div>
          )}

          {av.islakImzaBelgesi && av.islakImzaBelgesi.data && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Islak İmzalı Belge</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openBase64Doc(av.islakImzaBelgesi)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid={`hr-view-document-${req.id}`}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {av.islakImzaBelgesi.name || 'Belgeyi Görüntüle'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadBase64Doc(av.islakImzaBelgesi)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid={`hr-download-document-${req.id}`}
                >
                  <Download className="h-4 w-4 mr-2" />
                  İndir
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              onClick={() => handleApprove(req)}
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`hr-approve-avans-${req.id}`}
            >
              <CheckCircle className="h-4 w-4 mr-2" /> Onayla ve Muhasebeye İlet
            </Button>
            <Button
              onClick={() => { setRejectTarget(req); setRejectReason(''); }}
              disabled={actionLoading}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid={`hr-reject-avans-${req.id}`}
            >
              <XCircle className="h-4 w-4 mr-2" /> Reddet
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderRejectedCard = (req) => {
    const av = getAvans(req);
    const reason = req.hr_rejection?.reason || req.hr_rejection?.note || '-';
    const rejectedBy = req.hr_rejection?.rejected_by_name || '';
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-red-400 opacity-95" data-testid={`hr-rejected-request-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-red-100">
                <User className="h-4 w-4 text-red-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{req.requester_name || av.talepEdenAdi || 'Çalışan'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {formatDate(req.created_at)}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
              İK Reddetti
            </Badge>
          </div>

          {req.requester_branch_name && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Building2 className="h-3 w-3" /> {req.requester_branch_name}
            </p>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 rounded-lg p-3">
            <div>
              <p className="text-xs text-gray-500">Avans Miktarı</p>
              <p className="font-bold text-gray-700">{formatTL(av.avansMiktari)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Taksit Sayısı</p>
              <p className="font-medium">{av.taksitSayisi || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Form No</p>
              <p className="font-medium">{req.form_number || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Ödeme Tarihi</p>
              <p className="font-medium">{av.odemeTarihi || '-'}</p>
            </div>
          </div>

          {av.avansSebebi && (
            <div>
              <p className="text-xs text-gray-500">Avans Sebebi</p>
              <p className="text-sm text-gray-700">{av.avansSebebi}</p>
            </div>
          )}

          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-600 font-medium flex items-center gap-1">
              <XCircle className="h-3 w-3" /> Red Sebebi{rejectedBy ? ` (${rejectedBy})` : ''}
            </p>
            <p className="text-sm text-red-800 mt-1">{reason}</p>
          </div>

          {av.islakImzaBelgesi && av.islakImzaBelgesi.data && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Islak İmzalı Belge</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openBase64Doc(av.islakImzaBelgesi)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid={`hr-rejected-view-document-${req.id}`}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {av.islakImzaBelgesi.name || 'Belgeyi Görüntüle'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadBase64Doc(av.islakImzaBelgesi)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid={`hr-rejected-download-document-${req.id}`}
                >
                  <Download className="h-4 w-4 mr-2" />
                  İndir
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const jobStatusLabel = (s) => {
    switch (s) {
      case 'pending': return 'Muhasebe Onay Bekliyor';
      case 'approved': return 'Onaylandı (İşleniyor)';
      case 'prepared': return 'Hazırlanıyor';
      case 'todo': return 'Yapılacak İşlerde';
      case 'completed': return 'Tamamlandı';
      default: return 'Muhasebede';
    }
  };

  // ---- Fazla Mesai yardımcıları ----
  const overtimeDoc = (ot) => (ot.mesaiFormDosya ? { data: ot.mesaiFormDosya, name: ot.mesaiFormDosyaAdi || 'mesai_formu' } : null);

  const renderOvertimeDoc = (req, prefix) => {
    const ot = getAvans(req);
    const doc = overtimeDoc(ot);
    if (!doc || !doc.data) return null;
    return (
      <div>
        <p className="text-xs text-gray-500 mb-1">Islak İmzalı Form</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => openBase64Doc(doc)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`${prefix}-view-document-${req.id}`}>
            <FileText className="h-4 w-4 mr-2" />{doc.name}
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadBase64Doc(doc)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`${prefix}-download-document-${req.id}`}>
            <Download className="h-4 w-4 mr-2" />İndir
          </Button>
        </div>
      </div>
    );
  };

  const renderOvertimeInfoGrid = (ot, req) => (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 rounded-lg p-3">
        <div>
          <p className="text-xs text-gray-500">Mesai Tarihi</p>
          <p className="font-medium">{ot.mesaiTarihi || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Mesai Saati</p>
          <p className="font-bold text-amber-700">{ot.mesaiSaati ? `${ot.mesaiSaati} saat` : '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Mesai Günü</p>
          <p className="font-medium">{ot.mesaiGunu === 'tatil' ? 'Tatil' : ot.mesaiGunu === 'is_gunu' ? 'İş Günü' : '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Form No</p>
          <p className="font-medium">{req.form_number || '-'}</p>
        </div>
      </div>
      {ot.mesaiNedeni && (
        <div>
          <p className="text-xs text-gray-500">Mesai Nedeni</p>
          <p className="text-sm text-gray-700">{ot.mesaiNedeni}</p>
        </div>
      )}
    </>
  );

  const overtimeHeader = (req, ot, iconBg, iconColor) => (
    <div className="flex items-center gap-2">
      <div className={`p-2 rounded-lg ${iconBg}`}>
        <Clock className={`h-4 w-4 ${iconColor}`} />
      </div>
      <div>
        <p className="font-semibold text-gray-900">{req.requester_name || ot.mesaiYapanAdi || 'Çalışan'}</p>
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Calendar className="h-3 w-3" /> {formatDate(req.created_at)}
          {req.requester_branch_name ? ` · ${req.requester_branch_name}` : ''}
        </p>
      </div>
    </div>
  );

  const renderOvertimeCard = (req) => {
    const ot = getAvans(req);
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-amber-500" data-testid={`hr-overtime-request-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            {overtimeHeader(req, ot, 'bg-amber-100', 'text-amber-700')}
            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">İK Onayı Bekliyor</Badge>
          </div>
          {renderBranchApprovedLine(req)}
          {renderOvertimeInfoGrid(ot, req)}
          {renderOvertimeDoc(req, 'hr-overtime')}
          <div className="flex gap-2 pt-1">
            <Button onClick={() => handleApproveOvertime(req)} disabled={actionLoading}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid={`hr-approve-overtime-${req.id}`}>
              <CheckCircle className="h-4 w-4 mr-2" /> Onayla
            </Button>
            <Button onClick={() => { setRejectTarget(req); setRejectReason(''); }} disabled={actionLoading}
              variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`hr-reject-overtime-${req.id}`}>
              <XCircle className="h-4 w-4 mr-2" /> Reddet
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderOvertimeApprovedCard = (req) => {
    const ot = getAvans(req);
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-green-600" data-testid={`hr-overtime-approved-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            {overtimeHeader(req, ot, 'bg-green-100', 'text-green-700')}
            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" /> Onaylandı</Badge>
          </div>
          {renderOvertimeInfoGrid(ot, req)}
          {renderOvertimeDoc(req, 'hr-overtime-approved')}
        </CardContent>
      </Card>
    );
  };

  const renderOvertimeRejectedCard = (req) => {
    const ot = getAvans(req);
    const rej = req.hr_rejection || req.management_rejection || {};
    const reason = rej.reason || rej.note || '-';
    const byLabel = req.status === 'rejected_by_management' ? 'Yönetim' : 'İK';
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-red-400" data-testid={`hr-overtime-rejected-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            {overtimeHeader(req, ot, 'bg-red-100', 'text-red-700')}
            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200"><XCircle className="h-3 w-3 mr-1" /> {byLabel} Reddetti</Badge>
          </div>
          {renderOvertimeInfoGrid(ot, req)}
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-600 font-medium">Red Sebebi</p>
            <p className="text-sm text-red-800 mt-1">{reason}</p>
          </div>
          {renderOvertimeDoc(req, 'hr-overtime-rejected')}
        </CardContent>
      </Card>
    );
  };


  // ---- İzin yardımcıları ----
  const leaveHeader = (req, av, iconBg, iconColor) => (
    <div className="flex items-center gap-2">
      <div className={`p-2 rounded-lg ${iconBg}`}>
        <CalendarDays className={`h-4 w-4 ${iconColor}`} />
      </div>
      <div>
        <p className="font-semibold text-gray-900">{req.requester_name || av.talepEdenAdi || 'Çalışan'}</p>
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Calendar className="h-3 w-3" /> {formatDate(req.created_at)}
          {req.requester_branch_name ? ` · ${req.requester_branch_name}` : ''}
        </p>
      </div>
    </div>
  );

  const renderLeaveDoc = (req, prefix) => {
    const doc = getLeaveDoc(getAvans(req));
    if (!doc || !doc.data) return null;
    return (
      <div>
        <p className="text-xs text-gray-500 mb-1">Islak İmzalı Belge</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => openBase64Doc(doc)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`${prefix}-view-document-${req.id}`}>
            <FileText className="h-4 w-4 mr-2" />{doc.name || 'Belgeyi Görüntüle'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadBase64Doc(doc)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`${prefix}-download-document-${req.id}`}>
            <Download className="h-4 w-4 mr-2" />İndir
          </Button>
        </div>
      </div>
    );
  };

  const renderLeaveInfoGrid = (av) => (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 rounded-lg p-3">
        <div>
          <p className="text-xs text-gray-500">İzin Türü</p>
          <p className="font-medium">{izinTuruLabel(av.izinTuru)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Başlangıç</p>
          <p className="font-medium">{av.izinBaslangic || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Bitiş</p>
          <p className="font-medium">{av.izinBitis || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Sayılan Gün</p>
          <p className="font-bold text-sky-700">{av.hesaplananGun != null ? `${av.hesaplananGun} gün` : '-'}</p>
        </div>
      </div>
      {av.asanTuru === 'avans' && (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          <CalendarDays className="h-3 w-3 mr-1" /> Avans Yıllık İzin{av.asanGun != null ? ` (${av.asanGun} gün)` : ''}
        </Badge>
      )}
      {av.izinNedeni && (
        <div>
          <p className="text-xs text-gray-500">İzin Nedeni</p>
          <p className="text-sm text-gray-700">{av.izinNedeni}</p>
        </div>
      )}
    </>
  );

  const renderLeaveCard = (req) => {
    const av = getAvans(req);
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-sky-500" data-testid={`hr-leave-request-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            {leaveHeader(req, av, 'bg-sky-100', 'text-sky-700')}
            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">İK Onayı Bekliyor</Badge>
          </div>
          {renderBranchApprovedLine(req)}
          {renderLeaveInfoGrid(av)}
          {renderLeaveDoc(req, 'hr-leave')}
          <div className="flex gap-2 pt-1">
            <Button onClick={() => openLeaveApproval(req)} disabled={actionLoading}
              className="bg-sky-600 hover:bg-sky-700 text-white" data-testid={`hr-approve-leave-${req.id}`}>
              <CheckCircle className="h-4 w-4 mr-2" /> Onayla
            </Button>
            <Button onClick={() => { setRejectTarget(req); setRejectReason(''); }} disabled={actionLoading}
              variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`hr-reject-leave-${req.id}`}>
              <XCircle className="h-4 w-4 mr-2" /> Reddet
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderLeaveApprovedCard = (req) => {
    const av = getAvans(req);
    const statusLabel = req.hr_approval?.attendance_status_label
      || attendanceStatusOptions.find((o) => o.value === req.hr_approval?.attendance_status)?.label;
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-green-600" data-testid={`hr-leave-approved-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            {leaveHeader(req, av, 'bg-green-100', 'text-green-700')}
            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" /> Onaylandı{statusLabel ? ` · ${statusLabel}` : ''}</Badge>
          </div>
          {renderLeaveInfoGrid(av)}
          {req.hr_approval?.selected_days?.length ? (
            <p className="text-xs text-gray-500">İşlenen gün sayısı: <span className="font-medium text-gray-700">{req.hr_approval.selected_days.length}</span></p>
          ) : null}
          {renderLeaveDoc(req, 'hr-leave-approved')}
        </CardContent>
      </Card>
    );
  };

  const renderLeaveRejectedCard = (req) => {
    const av = getAvans(req);
    const rej = req.hr_rejection || req.management_rejection || {};
    const reason = rej.reason || rej.note || '-';
    const byLabel = req.status === 'rejected_by_management' ? 'Yönetim' : 'İK';
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-red-400" data-testid={`hr-leave-rejected-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            {leaveHeader(req, av, 'bg-red-100', 'text-red-700')}
            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200"><XCircle className="h-3 w-3 mr-1" /> {byLabel} Reddetti</Badge>
          </div>
          {renderLeaveInfoGrid(av)}
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-600 font-medium">Red Sebebi</p>
            <p className="text-sm text-red-800 mt-1">{reason}</p>
          </div>
          {renderLeaveDoc(req, 'hr-leave-rejected')}
        </CardContent>
      </Card>
    );
  };


  const renderDocButtons = (av, req, prefix) => {
    if (!(av.islakImzaBelgesi && av.islakImzaBelgesi.data)) return null;
    return (
      <div>
        <p className="text-xs text-gray-500 mb-1">Islak İmzalı Belge</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => openBase64Doc(av.islakImzaBelgesi)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`${prefix}-view-document-${req.id}`}>
            <FileText className="h-4 w-4 mr-2" />{av.islakImzaBelgesi.name || 'Belgeyi Görüntüle'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadBase64Doc(av.islakImzaBelgesi)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`${prefix}-download-document-${req.id}`}>
            <Download className="h-4 w-4 mr-2" />İndir
          </Button>
        </div>
      </div>
    );
  };

  const renderInfoGrid = (av, req) => (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 rounded-lg p-3">
        <div>
          <p className="text-xs text-gray-500">Avans Miktarı</p>
          <p className="font-bold text-emerald-700">{formatTL(av.avansMiktari)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Taksit Sayısı</p>
          <p className="font-medium">{av.taksitSayisi || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Form No</p>
          <p className="font-medium">{req.form_number || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Ödeme Tarihi</p>
          <p className="font-medium">{av.odemeTarihi || '-'}</p>
        </div>
      </div>
      {av.avansSebebi && (
        <div>
          <p className="text-xs text-gray-500">Avans Sebebi</p>
          <p className="text-sm text-gray-700">{av.avansSebebi}</p>
        </div>
      )}
    </>
  );

  const renderForwardedCard = (req) => {
    const av = getAvans(req);
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-blue-500" data-testid={`hr-forwarded-request-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-100">
                <Send className="h-4 w-4 text-blue-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{req.requester_name || av.talepEdenAdi || 'Çalışan'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {formatDate(req.created_at)}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
              <Clock className="h-3 w-3 mr-1" /> {jobStatusLabel(req.accounting_job_status)}
            </Badge>
          </div>
          {req.requester_branch_name && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Building2 className="h-3 w-3" /> {req.requester_branch_name}
              {req.accounting_job_code ? ` · İş Kodu: ${req.accounting_job_code}` : ''}
            </p>
          )}
          {renderInfoGrid(av, req)}
          {renderDocButtons(av, req, 'hr-forwarded')}
        </CardContent>
      </Card>
    );
  };

  const renderGivenCard = (req) => {
    const av = getAvans(req);
    return (
      <Card key={req.id} className="overflow-hidden border-l-4 border-l-green-600" data-testid={`hr-given-request-${req.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-100">
                <Banknote className="h-4 w-4 text-green-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{req.requester_name || av.talepEdenAdi || 'Çalışan'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {formatDate(req.created_at)}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
              <CheckCircle className="h-3 w-3 mr-1" /> Verildi
            </Badge>
          </div>
          {req.requester_branch_name && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Building2 className="h-3 w-3" /> {req.requester_branch_name}
              {req.accounting_job_code ? ` · İş Kodu: ${req.accounting_job_code}` : ''}
            </p>
          )}
          {renderInfoGrid(av, req)}
          {req.avans_given_at && (
            <p className="text-xs text-green-700">Verilme Tarihi: {formatDate(req.avans_given_at)}</p>
          )}
          {renderDocButtons(av, req, 'hr-given')}
        </CardContent>
      </Card>
    );
  };

  // İsim + tarih filtresi uygulanmış listeler
  const fAvans = applyFilters(avansRequests);
  const fForwarded = applyFilters(forwardedRequests);
  const fGiven = applyFilters(givenAdvances);
  const fRejected = applyFilters(rejectedRequests);
  const fOtInbox = applyFilters(overtimeInbox);
  const fOtApproved = applyFilters(overtimeApproved);
  const fOtRejected = applyFilters(overtimeRejected);
  const fLeaveInbox = applyFilters(leaveInbox);
  const fLeaveApproved = applyFilters(leaveApproved);
  const fLeaveRejected = applyFilters(leaveRejected);

  return (
    <div className="space-y-6" data-testid="hr-personnel-requests-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 shadow-lg">
            <ClipboardList className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Personel Talepleri</h1>
            <p className="text-sm text-gray-500">Şube yöneticilerinin onayladığı, İK onayı bekleyen talepler</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchRequests} data-testid="hr-refresh-personnel-requests">
          <RefreshCw className="h-4 w-4 mr-2" /> Yenile
        </Button>
      </div>

      {/* Arama & Filtreleme (isim + tarih) - tüm sekmelerde geçerli */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white border border-gray-200 rounded-lg p-3" data-testid="hr-personnel-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="İsme göre ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="hr-personnel-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <Input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="w-44"
            data-testid="hr-personnel-date-input"
          />
        </div>
        {filtersActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setSearchTerm(''); setFilterDate(''); }}
            className="text-gray-600"
            data-testid="hr-personnel-clear-filters"
          >
            <X className="h-4 w-4 mr-1" /> Filtreyi Temizle
          </Button>
        )}
      </div>

      <Tabs defaultValue="avans" className="w-full">
        <TabsList>
          <TabsTrigger value="avans" data-testid="hr-tab-avans">
            <Wallet className="h-4 w-4 mr-2" />
            Avans Talepleri
            {avansRequests.length > 0 && (
              <Badge className="ml-2 bg-emerald-600 text-white">{avansRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="overtime" data-testid="hr-tab-overtime">
            <Clock className="h-4 w-4 mr-2" />
            Fazla Mesai Talepleri
            {overtimeInbox.length > 0 && (
              <Badge className="ml-2 bg-amber-600 text-white">{overtimeInbox.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="leave" data-testid="hr-tab-leave">
            <CalendarDays className="h-4 w-4 mr-2" />
            İzin Talepleri
            {leaveInbox.length > 0 && (
              <Badge className="ml-2 bg-sky-600 text-white">{leaveInbox.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* AVANS */}
        <TabsContent value="avans" className="mt-4">
          <Tabs defaultValue="avans-bekleyen" className="w-full">
            <TabsList>
              <TabsTrigger value="avans-bekleyen" data-testid="hr-subtab-avans-bekleyen">
                Bekleyen
                {avansRequests.length > 0 && <Badge className="ml-2 bg-yellow-500 text-white">{avansRequests.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="avans-iletilen" data-testid="hr-subtab-avans-iletilen">
                İletilen Talepler
                {forwardedRequests.length > 0 && <Badge className="ml-2 bg-blue-600 text-white">{forwardedRequests.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="avans-verilen" data-testid="hr-subtab-avans-verilen">
                Verilen Avanslar
                {givenAdvances.length > 0 && <Badge className="ml-2 bg-green-600 text-white">{givenAdvances.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="avans-red" data-testid="hr-subtab-avans-red">
                Reddedilenler
                {rejectedRequests.length > 0 && <Badge className="ml-2 bg-red-600 text-white">{rejectedRequests.length}</Badge>}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="avans-bekleyen" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : fAvans.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><Wallet className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{filtersActive ? 'Filtreye uygun avans talebi bulunamadı' : 'İK onayı bekleyen avans talebi yok'}</p></CardContent></Card>
              ) : fAvans.map(renderAvansCard)}
            </TabsContent>
            <TabsContent value="avans-iletilen" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : fForwarded.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><Send className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{filtersActive ? 'Filtreye uygun talep bulunamadı' : 'Muhasebeye iletilen talep yok'}</p></CardContent></Card>
              ) : fForwarded.map(renderForwardedCard)}
            </TabsContent>
            <TabsContent value="avans-verilen" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : fGiven.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><Banknote className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{filtersActive ? 'Filtreye uygun avans bulunamadı' : 'Verilen avans yok'}</p></CardContent></Card>
              ) : fGiven.map(renderGivenCard)}
            </TabsContent>
            <TabsContent value="avans-red" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : fRejected.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><XCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{filtersActive ? 'Filtreye uygun talep bulunamadı' : 'Reddedilen avans talebi yok'}</p></CardContent></Card>
              ) : fRejected.map(renderRejectedCard)}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* FAZLA MESAI */}
        <TabsContent value="overtime" className="mt-4">
          <Tabs defaultValue="overtime-bekleyen" className="w-full">
            <TabsList>
              <TabsTrigger value="overtime-bekleyen" data-testid="hr-subtab-overtime-bekleyen">
                Bekleyen
                {overtimeInbox.length > 0 && <Badge className="ml-2 bg-yellow-500 text-white">{overtimeInbox.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="overtime-onaylanan" data-testid="hr-subtab-overtime-onaylanan">
                Onaylanan
                {overtimeApproved.length > 0 && <Badge className="ml-2 bg-green-600 text-white">{overtimeApproved.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="overtime-red" data-testid="hr-subtab-overtime-red">
                Reddedilenler
                {overtimeRejected.length > 0 && <Badge className="ml-2 bg-red-600 text-white">{overtimeRejected.length}</Badge>}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="overtime-bekleyen" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : fOtInbox.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><Clock className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{filtersActive ? 'Filtreye uygun fazla mesai talebi bulunamadı' : 'İK onayı bekleyen fazla mesai talebi yok'}</p></CardContent></Card>
              ) : fOtInbox.map(renderOvertimeCard)}
            </TabsContent>
            <TabsContent value="overtime-onaylanan" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : fOtApproved.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><CheckCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{filtersActive ? 'Filtreye uygun fazla mesai talebi bulunamadı' : 'Onaylanan fazla mesai talebi yok'}</p></CardContent></Card>
              ) : fOtApproved.map(renderOvertimeApprovedCard)}
            </TabsContent>
            <TabsContent value="overtime-red" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : fOtRejected.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><XCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{filtersActive ? 'Filtreye uygun fazla mesai talebi bulunamadı' : 'Reddedilen fazla mesai talebi yok'}</p></CardContent></Card>
              ) : fOtRejected.map(renderOvertimeRejectedCard)}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* İZİN */}
        <TabsContent value="leave" className="mt-4">
          <Tabs defaultValue="leave-bekleyen" className="w-full">
            <TabsList>
              <TabsTrigger value="leave-bekleyen" data-testid="hr-subtab-leave-bekleyen">
                Bekleyen
                {leaveInbox.length > 0 && <Badge className="ml-2 bg-yellow-500 text-white">{leaveInbox.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="leave-onaylanan" data-testid="hr-subtab-leave-onaylanan">
                Onaylanan
                {leaveApproved.length > 0 && <Badge className="ml-2 bg-green-600 text-white">{leaveApproved.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="leave-red" data-testid="hr-subtab-leave-red">
                Reddedilenler
                {leaveRejected.length > 0 && <Badge className="ml-2 bg-red-600 text-white">{leaveRejected.length}</Badge>}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="leave-bekleyen" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : fLeaveInbox.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><CalendarDays className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{filtersActive ? 'Filtreye uygun izin talebi bulunamadı' : 'İK onayı bekleyen izin talebi yok'}</p></CardContent></Card>
              ) : fLeaveInbox.map(renderLeaveCard)}
            </TabsContent>
            <TabsContent value="leave-onaylanan" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : fLeaveApproved.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><CheckCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{filtersActive ? 'Filtreye uygun izin talebi bulunamadı' : 'Onaylanan izin talebi yok'}</p></CardContent></Card>
              ) : fLeaveApproved.map(renderLeaveApprovedCard)}
            </TabsContent>
            <TabsContent value="leave-red" className="mt-4 space-y-3">
              {loading ? <p className="text-sm text-gray-500 py-8 text-center">Yükleniyor...</p>
                : fLeaveRejected.length === 0 ? (
                <Card><CardContent className="py-12 text-center"><XCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{filtersActive ? 'Filtreye uygun izin talebi bulunamadı' : 'Reddedilen izin talebi yok'}</p></CardContent></Card>
              ) : fLeaveRejected.map(renderLeaveRejectedCard)}
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* İzin Onay Modalı */}
      <Dialog open={!!leaveTarget} onOpenChange={(o) => { if (!o) { setLeaveTarget(null); setSelectedLeaveDays([]); setLeaveNote(''); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hr-leave-approval-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-sky-600" /> İzin Günlerini Seç ve Onayla
            </DialogTitle>
            <DialogDescription>
              Seçilen günler çalışanın mesai çizelgesinde aşağıda belirlenen durumla işaretlenecektir.
            </DialogDescription>
          </DialogHeader>
          {leaveTarget && (() => {
            const av = getAvans(leaveTarget);
            const isAvansLeave = av.asanTuru === 'avans';
            const days = [];
            if (av.izinBaslangic && av.izinBitis) {
              const cur = new Date(av.izinBaslangic);
              const end = new Date(av.izinBitis);
              let guard = 0;
              while (cur <= end && guard < 400) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); guard += 1; }
            }
            return (
              <div className="space-y-4">
                <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Talep Eden:</span> <span className="font-medium">{leaveTarget.requester_name || av.talepEdenAdi || '-'}</span></div>
                  <div><span className="text-gray-500">İzin Türü:</span> <span className="font-medium">{izinTuruLabel(av.izinTuru)}</span></div>
                  <div><span className="text-gray-500">Tarih:</span> <span className="font-medium">{av.izinBaslangic} → {av.izinBitis}</span></div>
                  <div><span className="text-gray-500">Sayılan Gün:</span> <span className="font-medium">{av.hesaplananGun != null ? `${av.hesaplananGun} gün` : '-'}</span></div>
                  {isAvansLeave && (
                    <div className="col-span-2">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        <CalendarDays className="h-3 w-3 mr-1" /> Avans Yıllık İzin{av.asanGun != null ? ` (${av.asanGun} gün)` : ''}
                      </Badge>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">İzinli Günleri Seçin ({selectedLeaveDays.length} gün seçildi)</Label>
                  <div className="grid grid-cols-7 gap-2 p-3 bg-gray-50 rounded-lg border max-h-[260px] overflow-y-auto">
                    {days.map((d) => {
                      const dayStr = d.toISOString().split('T')[0];
                      const isSelected = selectedLeaveDays.includes(dayStr);
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      return (
                        <button key={dayStr} type="button" onClick={() => toggleLeaveDay(dayStr)}
                          data-testid={`hr-leave-day-${dayStr}`}
                          className={`p-2 rounded-lg text-center transition-colors ${isSelected ? 'bg-sky-600 text-white shadow-md' : isWeekend ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-white text-gray-700 hover:bg-sky-100 border'}`}>
                          <div className="text-xs font-medium">{['Pz','Pt','Sa','Ça','Pe','Cu','Ct'][d.getDay()]}</div>
                          <div className="text-base font-bold">{d.getDate()}</div>
                          <div className="text-xs">{d.toLocaleDateString('tr-TR', { month: 'short' })}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Mesai Çizelgesine İşlenecek Durum</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {attendanceStatusOptions.map((option) => (
                      <button key={option.value} type="button" onClick={() => setSelectedAttendanceStatus(option.value)}
                        data-testid={`hr-leave-status-${option.value}`}
                        className={`p-2 rounded-lg text-center text-sm font-medium transition-colors border-2 ${selectedAttendanceStatus === option.value ? `${option.color} ring-2 ring-offset-1 ring-gray-400` : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}>
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {isAvansLeave && selectedAttendanceStatus === 'annual' && (
                    <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                      ℹ️ Avans yıllık izin: Bu günler yıllık izin olarak işlenir; çalışanın kalan izin bakiyesi eksiye düşer (ileride hak edeceği izinden mahsup edilir).
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="hr-leave-note">Not (İsteğe Bağlı)</Label>
                  <Textarea id="hr-leave-note" value={leaveNote} onChange={(e) => setLeaveNote(e.target.value)} rows={2}
                    placeholder="Onay notu..." data-testid="hr-leave-note-input" />
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setLeaveTarget(null); setSelectedLeaveDays([]); setLeaveNote(''); }}>İptal</Button>
            <Button onClick={confirmLeaveApproval} disabled={actionLoading}
              className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="hr-confirm-leave-btn">
              <CheckCircle className="h-4 w-4 mr-2" /> Onayla ve İşle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reddet Modal */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(''); } }}>
        <DialogContent data-testid="hr-reject-modal">
          <DialogHeader>
            <DialogTitle>Talebi Reddet</DialogTitle>
            <DialogDescription>
              {rejectTarget?.requester_name} adlı çalışanın talebini reddediyorsunuz. Lütfen sebep belirtin.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Red sebebini yazın..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            data-testid="hr-reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>İptal</Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="hr-confirm-reject-btn"
            >
              Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRPersonnelRequests;
